import { Alert, Box, TextField, Typography } from '@evaneos/b2b-ui';
import { ChangeEvent, FormEvent, useState } from 'react';
import { useIntl } from 'react-intl';

import EyeIcon from '@evaneos/b2b-ui/icons/VisibilityOutlined';

import { DisplayProject } from '../../../../resources/dossier-overview/types';

import { useUpdateRequesterInformationMutation } from '../../../../resources/dossier/mutations';
import { DossierId } from '../../../../resources/dossier/types';
import { DefinitionList } from '../../../../design-system/DefinitionList/DefinitionList';
import {
    getMaskedPhoneNumber,
    phoneNumberFromString,
    validatePhoneNumber,
} from '../../../../shared/utilities/phone';
import { useDossierLayoutInfo } from '../../DossierLayoutInfoContext';
import { useGetTrackedTripProperties, useTrackEventAmplitude } from '../../../../shared/analytics';

type PhoneNumberEditableItemProps = {
    project: DisplayProject | undefined;
    dossierId: DossierId;
    labelId: string;
};

export function PhoneNumberEditableItem({
    project,
    dossierId,
    labelId,
}: PhoneNumberEditableItemProps) {
    const { formatMessage } = useIntl();
    const [phoneValue, setPhoneValue] = useState<string>(project?.requesterPhone ?? '');
    const [hasPhoneError, setHasPhoneError] = useState(false);
    const updateRequesterInformationMutation = useUpdateRequesterInformationMutation(dossierId);
    const { isFirstColumnNarrow } = useDossierLayoutInfo();
    const { trackEvent } = useTrackEventAmplitude();
    const trackedTripProperties = useGetTrackedTripProperties(dossierId);

    const handlePhoneNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value;

        if (e.target.checkValidity() === false) {
            return;
        }

        setPhoneValue(inputValue);

        if (inputValue === '') {
            setHasPhoneError(false);
            return;
        }

        const phoneNumber = phoneNumberFromString(inputValue);

        if (validatePhoneNumber(phoneNumber)) {
            setHasPhoneError(false);
        } else {
            setHasPhoneError(true);
        }
    };

    const handleBeforeClose = () => {
        if (!project) return true;

        const phoneNumber = phoneNumberFromString(phoneValue);

        if (phoneValue !== '' && !validatePhoneNumber(phoneNumber)) {
            setHasPhoneError(true);
            return false;
        }

        const formattedPhoneNumber = phoneNumber?.number || '';

        if (
            formattedPhoneNumber !== project.requesterPhone &&
            !updateRequesterInformationMutation.isPending
        ) {
            updateRequesterInformationMutation.mutate({
                name: project.requesterName,
                phoneNumber: formattedPhoneNumber,
            });

            trackEvent((ampli, defaultProperties) => {
                ampli.travelerInformationChanged({
                    ...defaultProperties,
                    ...trackedTripProperties,
                    edited_field: 'phone number',
                });
            });
        } else {
            trackEvent((ampli, defaultProperties) => {
                ampli.phoneNumberViewed({
                    ...defaultProperties,
                    ...trackedTripProperties,
                });
            });
        }

        setHasPhoneError(false);
        return true;
    };

    const handleSubmit = (e: FormEvent<HTMLFormElement>, close: () => void) => {
        e.preventDefault();
        close();
    };

    return (
        <DefinitionList.EditableValue
            labelId={labelId}
            popoverWidth="trigger"
            placement="over"
            onBeforeClose={handleBeforeClose}
            hoverHint={
                <Typography
                    sx={{
                        fontSize: '1.2rem',
                        fontWeight: '600',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                    }}
                    color="text.secondary"
                    component="span"
                >
                    <EyeIcon
                        sx={{ fontSize: '1.4rem' }}
                        aria-labelledby="reveal-phone-number-text-hint"
                    />
                    <Box
                        component="span"
                        sx={{ display: isFirstColumnNarrow ? 'none' : 'block' }}
                        id="reveal-phone-number-text-hint"
                    >
                        {formatMessage({
                            id: 'dossier.information.traveller.click_to_reveal_phone',
                        })}
                    </Box>
                </Typography>
            }
            editor={({ close }) => (
                <Box component="form" position="relative" onSubmit={(e) => handleSubmit(e, close)}>
                    <TextField
                        name="phone-number"
                        type="tel"
                        error={hasPhoneError}
                        slotProps={{
                            htmlInput: {
                                'data-testid': 'input-phone-number',
                                'aria-labelledby': labelId,
                                'aria-invalid': hasPhoneError,
                                'aria-describedby': hasPhoneError ? 'phone-error' : undefined,
                                pattern: '^\\+?[0-9-\\. ]*$',
                                sx: {
                                    fontSize: '1.4rem',
                                },
                            },
                        }}
                        size="small"
                        defaultValue={project?.requesterPhone ?? ''}
                        onChange={handlePhoneNumberChange}
                        fullWidth
                        autoFocus
                    />
                    {hasPhoneError && (
                        <Alert
                            id="phone-error"
                            severity="error"
                            sx={{ position: 'absolute', top: '110%', left: 0, right: 0 }}
                        >
                            <Typography variant="body2" color="error.700">
                                {formatMessage({
                                    id: 'dossier.information.traveller.update.error.invalid_phone_number_with_country_code',
                                })}
                            </Typography>
                        </Alert>
                    )}
                </Box>
            )}
        >
            {project?.requesterPhone ? (
                <Typography variant="body2" component="span">
                    {getMaskedPhoneNumber(project.requesterPhone)}
                </Typography>
            ) : (
                '-'
            )}
        </DefinitionList.EditableValue>
    );
}
